import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export const cn = (...inputs: ClassValue[]): string => twMerge(clsx(inputs));

/** "1:04:12" past an hour, "4:12" below it. */
export const formatDuration = (totalSeconds: number): string => {
  if (!Number.isFinite(totalSeconds) || totalSeconds < 0) return "0:00";
  const seconds = Math.floor(totalSeconds % 60);
  const minutes = Math.floor(totalSeconds / 60) % 60;
  const hours = Math.floor(totalSeconds / 3600);
  const mm = hours > 0 ? String(minutes).padStart(2, "0") : String(minutes);
  const ss = String(seconds).padStart(2, "0");
  return hours > 0 ? `${hours}:${mm}:${ss}` : `${mm}:${ss}`;
};

export const formatBytes = (bytes: number): string => {
  if (!Number.isFinite(bytes) || bytes <= 0) return "—";
  const units = ["B", "KB", "MB", "GB", "TB"];
  const exponent = Math.min(units.length - 1, Math.floor(Math.log(bytes) / Math.log(1024)));
  const value = bytes / 1024 ** exponent;
  return `${value.toFixed(value >= 10 || exponent === 0 ? 0 : 1)} ${units[exponent]}`;
};

export const formatDate = (ms: number): string => {
  if (!Number.isFinite(ms) || ms <= 0) return "";
  return new Date(ms).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric"
  });
};

/** Absolute date + time for tooltips (e.g. share expiry). */
export const formatDateTime = (ms: number): string => {
  if (!Number.isFinite(ms) || ms <= 0) return "";
  return new Date(ms).toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit"
  });
};

/** "Expires in 5 minutes" / "Expires in 2 months" for a future timestamp. */
export const formatExpiresIn = (ms: number, nowMs = Date.now()): string => {
  if (!Number.isFinite(ms) || ms <= 0) return "";
  const deltaSec = Math.round((ms - nowMs) / 1000);
  if (deltaSec <= 0) return "Expired";

  const units: [Intl.RelativeTimeFormatUnit, number][] = [
    ["second", 60],
    ["minute", 3600],
    ["hour", 86_400],
    ["day", 2_629_800],
    ["month", 31_557_600],
    ["year", Number.POSITIVE_INFINITY]
  ];
  const format = new Intl.RelativeTimeFormat(undefined, { numeric: "always" });
  let divisor = 1;
  for (const [unit, limit] of units) {
    if (deltaSec < limit) {
      return `Expires ${format.format(Math.max(1, Math.round(deltaSec / divisor)), unit)}`;
    }
    divisor = limit;
  }
  return `Expires ${formatDate(ms)}`;
};

/** "3 days ago" for anything recent, an absolute date once that stops helping. */
export const relativeTime = (ms: number): string => {
  if (!Number.isFinite(ms) || ms <= 0) return "";
  const deltaSec = Math.round((ms - Date.now()) / 1000);
  const magnitude = Math.abs(deltaSec);
  const units: [Intl.RelativeTimeFormatUnit, number][] = [
    ["second", 60],
    ["minute", 3600],
    ["hour", 86_400],
    ["day", 604_800],
    ["week", 2_629_800]
  ];
  if (magnitude >= 2_629_800) return formatDate(ms);
  const format = new Intl.RelativeTimeFormat(undefined, { numeric: "auto" });
  let divisor = 1;
  for (const [unit, limit] of units) {
    if (magnitude < limit) return format.format(Math.round(deltaSec / divisor), unit);
    divisor = limit;
  }
  return formatDate(ms);
};
